/**
 * AssessmentHelpDialog — a one-time "how to assess" coach mark shown on the
 * department-assessment screen. Explains what to do (comment the fragments
 * marked with a department badge) and how (select → write → Send).
 *
 * Auto-opens on first visit (the parent tracks dismissal in localStorage) and
 * can be reopened anytime via the Help button in the screen header.
 */

import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Typography,
} from "@mui/material";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { Localized } from "@fluent/react";

import { useAppSelector } from "../../../../app/hooks";
import { selectLoggedUser } from "../../../user/userSlice";

interface Props {
    open: boolean;
    /** Called on dismiss; `dontShowAgain` reflects the checkbox state. */
    onClose: (dontShowAgain: boolean) => void;
}

const STEPS: { id: string; fallback: string }[] = [
    { id: "assess-help-step1", fallback: "Fragments with a department badge are the ones to review." },
    { id: "assess-help-step2", fallback: "Click a highlighted fragment to open it in the panel on the right." },
    { id: "assess-help-step3", fallback: "Write your comment and press Send." },
];

export const AssessmentHelpDialog: React.FC<Props> = ({ open, onClose }) => {
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const userName = useAppSelector(selectLoggedUser);

    // Reset the checkbox to its default each time the dialog is (re)opened.
    useEffect(() => {
        if (open) setDontShowAgain(false);
    }, [open]);

    const close = () => onClose(dontShowAgain);

    return (
        <Dialog open={open} onClose={close} maxWidth="sm" fullWidth PaperProps={{ sx: { maxWidth: 520 } }}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
                <AssignmentTurnedInOutlinedIcon color="primary" />
                <Localized id="assess-help-title">
                    <span>_Welcome to the assessment</span>
                </Localized>
                <IconButton
                    aria-label="close"
                    onClick={close}
                    size="small"
                    sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {userName && (
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                        <Localized id="assess-help-greeting" vars={{ name: userName }}>
                            <span>{`_Hi, ${userName} 👋`}</span>
                        </Localized>
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <Localized id="assess-help-intro">
                        _Review the fragments assigned to your department and leave a comment on each.
                    </Localized>
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {STEPS.map((step, i) => (
                        <Box key={step.id} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 26,
                                    height: 26,
                                    flexShrink: 0,
                                    borderRadius: "50%",
                                    bgcolor: "primary.main",
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 13,
                                    fontWeight: 700,
                                }}
                            >
                                {i + 1}
                            </Box>
                            <Typography variant="body2" sx={{ pt: 0.25 }}>
                                <Localized id={step.id}>
                                    <span>{`_${step.fallback}`}</span>
                                </Localized>
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box
                    sx={{
                        mt: 2,
                        p: 1.25,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        borderRadius: 2,
                        bgcolor: "var(--blue-50)",
                        border: "1px solid var(--blue-200)",
                    }}
                >
                    <LightbulbOutlinedIcon fontSize="small" sx={{ color: "var(--blue-600)", mt: 0.125 }} />
                    <Typography variant="body2" color="text.secondary">
                        <Localized id="assess-help-tip">
                            _Tip: use the department filter to show only your fragments.
                        </Localized>
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={dontShowAgain}
                            onChange={(e) => setDontShowAgain(e.target.checked)}
                        />
                    }
                    label={
                        <Typography variant="body2" color="text.secondary">
                            <Localized id="assess-help-dont-show"><span>_Don't show again</span></Localized>
                        </Typography>
                    }
                />
                <Button variant="contained" onClick={close}>
                    <Localized id="assess-help-got-it"><span>_Got it</span></Localized>
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssessmentHelpDialog;
